import styled, { css } from 'styled-components';

import { media } from '@savgroup-front-common/constants';
import {
  tint,
  transparentize,
} from '@savgroup-front-common/primitive/helpers/cssColor';
import { fontWeight } from '@savgroup-front-common/primitive/helpers/fontWeight';
import { spacing } from '@savgroup-front-common/primitive/helpers/spacing';
import { rem } from '@savgroup-front-common/primitive/helpers/units';
import { SUPPORTED_PROPERTIES } from '@savgroup-front-common/types';

import {
  getCustomComponentProperty,
  styledGetCustomComponentProperty,
} from '../../../helpers';
import { fontStyle } from '../../../theme/helpers/fontStyle';
import { ACCORDION_ROLE } from '../Accordion.types';

interface $AccordionHeaderProps {
  $isErrored: boolean;
  $isActive: boolean;
  $isDisabled: boolean;
  $isList?: boolean;
  $role: ACCORDION_ROLE;
  $componentThemeName?: string;
  $isColored: boolean;
  $hasAdditionalTitleContent: boolean;
}

export const $AccordionHeader = styled.div<$AccordionHeaderProps>`
  width: 100%;

  display: flex;
  gap: ${spacing('sm')};
  align-items: ${({ $hasAdditionalTitleContent }) =>
    $hasAdditionalTitleContent ? 'start' : 'center'};
  justify-content: space-between;
  background-color: ${({
    $role,
    theme,
    $isErrored,
    $isActive,
    $isDisabled,
    $componentThemeName,
    $isList,
    $isColored,
  }) => {
    if ($isColored && $isActive) {
      return transparentize(0.9, theme.colors.primary);
    }

    if ($isList && !$isActive) {
      return theme.systemColors.defaultBackground;
    }

    if ($componentThemeName) {
      const getBackgroundColor = styledGetCustomComponentProperty({
        componentName: $componentThemeName,
        property: SUPPORTED_PROPERTIES.BACKGROUND_COLOR,
      });
      const backgroundColor = getBackgroundColor({ theme });

      if (backgroundColor) {
        return backgroundColor;
      }
    }
    if ($isDisabled) {
      return $isActive
        ? theme.colors.default
        : theme.systemColors.secondaryBackground;
    }
    if ($isErrored) {
      return $isActive ? theme.colors.danger : tint(0.85, theme.colors.danger);
    }

    switch ($role) {
      case ACCORDION_ROLE.PRIMARY:
        return theme.colors.headerTitleColor;
      case ACCORDION_ROLE.SECONDARY:
        return theme.colors.secondaryColor2;
      default:
        throw new Error('not supported role.');
    }
  }};

  border: ${({ theme }) => {
    return `1px solid ${theme.newUI.dividerColors.default}`;
  }};

  border-radius: ${({ $isActive, $componentThemeName, theme, $isList }) => {
    if ($isList) {
      return `0`;
    }

    return $isActive
      ? `${
          getCustomComponentProperty({
            theme,
            componentName: $componentThemeName,
            property: SUPPORTED_PROPERTIES.BORDER_RADIUS,
          }) || spacing(1.5)
        } ${
          getCustomComponentProperty({
            theme,
            componentName: $componentThemeName,
            property: SUPPORTED_PROPERTIES.BORDER_RADIUS,
          }) || spacing(1.5)
        } 0 0`
      : `${
          getCustomComponentProperty({
            theme,
            componentName: $componentThemeName,
            property: SUPPORTED_PROPERTIES.BORDER_RADIUS,
          }) || spacing(1.5)
        } ${
          getCustomComponentProperty({
            theme,
            componentName: $componentThemeName,
            property: SUPPORTED_PROPERTIES.BORDER_RADIUS,
          }) || spacing(1.5)
        }`;
  }};

  height: ${({ $componentThemeName, $hasAdditionalTitleContent }) => {
    if ($hasAdditionalTitleContent) {
      return `${rem(70)}`;
    }

    return styledGetCustomComponentProperty({
      property: SUPPORTED_PROPERTIES.HEIGHT,
      componentName: $componentThemeName,
      fallback: `${spacing(12)}`,
    });
  }};
  padding: ${({ $hasAdditionalTitleContent }) =>
    $hasAdditionalTitleContent
      ? `${spacing(3.5)} ${spacing(3.5)}`
      : `0 ${spacing(3.5)}`};
  cursor: ${({ $isDisabled }) => ($isDisabled ? undefined : 'pointer')};

  @media ${media.maxWidth.sm} {
    padding: ${spacing('lg')};
  }
`;

interface $AccordionTitleProps {
  $color: string;
  $isList?: boolean;
}
export const $AccordionTitle = styled.div<$AccordionTitleProps>`
  color: ${({ $color }) => $color};
  display: flex;
  flex-direction: row;
  width: 100%;
  min-width: 0;
`;

export const $AccordionHeaderAdditionalTitleContent = styled.div`
  color: ${({ theme }) => theme.newUI.defaultColors.secondaryText};
  ${fontStyle('xs')};
  font-weight: ${fontWeight('normal')};
`;

const side = css`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
`;

export const $LeftSideContainer = styled.div<{
  $isList?: boolean;
}>`
  ${side};
  font-weight: ${fontWeight('bold')};
  user-select: none;

  display: flex;
  flex-direction: column;
  align-items: start;
  margin-right: auto;
  flex: 1 1 auto;
  min-width: 0;
  ${({ $isList }) => ($isList ? 'width: 100%' : '')}
`;

export const $AccordionHeaderAdditionalContent = styled.div`
  color: ${({ theme }) => theme.newUI.defaultColors.secondaryText};
  ${fontStyle('xs')};
  font-weight: ${fontWeight('normal')};
`;

export const $RightSideContainer = styled.div`
  ${side}
  flex-shrink: 0;
  >svg {
    margin-left: ${spacing('lg')};
  }
`;
